import { getApps, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth, User } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { getMessaging, getToken, isSupported, Messaging } from "firebase/messaging";
import { getAnalytics, logEvent, setUserId, Analytics } from "firebase/analytics";
import { FUNCTION_URLS } from "./config/functions";

// Type definitions for Vite environment variables
interface ImportMetaEnv {
  readonly VITE_FIREBASE_API_KEY: string;
  readonly VITE_FIREBASE_AUTH_DOMAIN: string;
  readonly VITE_FIREBASE_PROJECT_ID: string;
  readonly VITE_FIREBASE_STORAGE_BUCKET: string;
  readonly VITE_FIREBASE_MESSAGING_SENDER_ID: string;
  readonly VITE_FIREBASE_APP_ID: string;
  readonly VITE_FIREBASE_MEASUREMENT_ID?: string;
  readonly VITE_FIREBASE_VAPID_KEY?: string;
  readonly VITE_FUNCTIONS_BASE_URL?: string;
  readonly VITE_USE_EMULATOR?: string;
  readonly VITE_EMULATOR_AUTH_PORT?: string;
  readonly VITE_EMULATOR_FUNCTIONS_PORT?: string;
  readonly VITE_EMULATOR_FIRESTORE_PORT?: string;
}

declare global {
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// Check if we should use emulator (only when explicitly enabled via VITE_USE_EMULATOR=true)
// By default, connects to production: never-eat-alone-b2e0e
const useEmulator = import.meta.env.VITE_USE_EMULATOR === "true";

// Production Firebase configuration for never-eat-alone-b2e0e
// This is the production project - emulators are only used when VITE_USE_EMULATOR=true
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "fake-api-key",
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    "never-eat-alone-b2e0e.firebaseapp.com",
  projectId:
    import.meta.env.VITE_FIREBASE_PROJECT_ID || "never-eat-alone-b2e0e",
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
    "never-eat-alone-b2e0e.firebasestorage.app",
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "fake-sender-id",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "fake-app-id",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || undefined,
};

const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const functions = getFunctions(app, 'asia-southeast1'); // Set correct region
export const db = getFirestore(app);
const LOCAL_FCM_TOKEN_KEY = "BlinkBli:fcmToken";
let messagingPromise: Promise<Messaging | null> | null = null;

// Analytics - lazy initialization (only when first accessed)
let analyticsInstance: Analytics | null = null;
let analyticsInitialized = false;

// Helper to get analytics instance (lazy initialization on first access)
export function getAnalyticsInstance(): Analytics | null {
  if (!analyticsInitialized && typeof window !== "undefined") {
    analyticsInitialized = true;
    console.log("📊 Lazy initializing Firebase Analytics...");
    console.log("📊 Firebase config:", {
      projectId: firebaseConfig.projectId,
      appId: firebaseConfig.appId?.substring(0, 20) + "...",
      measurementId: firebaseConfig.measurementId
    });

    try {
      analyticsInstance = getAnalytics(app);
      console.log("📊 Firebase Analytics initialized successfully!", analyticsInstance);
    } catch (e: any) {
      console.error("📊 Firebase Analytics initialization FAILED:", e?.message || e);
      console.error("📊 Full error:", e);
    }
  }

  return analyticsInstance;
}

// Set user ID for analytics (for retention tracking)
export function setAnalyticsUserId(userId: string | null): void {
  const analytics = getAnalyticsInstance();
  if (analytics) {
    setUserId(analytics, userId);
    console.log(`📊 Analytics User ID set: ${userId}`);
  } else {
    console.log(`📊 [Analytics - not initialized] Cannot set user ID: ${userId}`);
  }
}

export { logEvent };

// Log environment being used
if (useEmulator) {
  console.log("🔧 Using Firebase Emulator Suite (local development)");
} else {
  console.log("🚀 Using Production Firebase: never-eat-alone-b2e0e");
}

// Connect to emulators only if explicitly enabled
if (useEmulator) {
  const authPort = parseInt(import.meta.env.VITE_EMULATOR_AUTH_PORT || "9099");
  const functionsPort = parseInt(
    import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT || "5001",
  );
  const firestorePort = parseInt(
    import.meta.env.VITE_EMULATOR_FIRESTORE_PORT || "8080",
  );

  console.log("🔧 Using Firebase Emulator Suite");
  console.log(`   Auth: localhost:${authPort}`);
  console.log(`   Functions: localhost:${functionsPort}`);
  console.log(`   Firestore: localhost:${firestorePort}`);

  connectAuthEmulator(auth, `http://localhost:${authPort}`, {
    disableWarnings: true,
  });
  connectFunctionsEmulator(functions, "localhost", functionsPort);
  connectFirestoreEmulator(db, "localhost", firestorePort);
}

/**
 * Helper function to call a Cloud Run callable function with authentication
 * Formats the request exactly like Firebase callable functions expect
 * @param functionName - Name of the function (must be a callable function)
 * @param data - Data to send to the function
 * @param user - Optional user object, defaults to current user from auth
 * @returns Promise with the function response
 */
export async function callCloudRunFunction<T = any, R = any>(
  functionName: keyof typeof FUNCTION_URLS,
  data: T,
  user?: User | null,
): Promise<R> {
  const url = FUNCTION_URLS[functionName];
  const currentUser = user || auth.currentUser;

  if (!currentUser) {
    console.error("🔐 callCloudRunFunction - No user found");
    throw new Error("User must be authenticated to call this function");
  }

  console.log(
    "🔐 callCloudRunFunction - Getting token for user:",
    currentUser.uid,
  );
  const token = await currentUser.getIdToken();

  if (!token) {
    console.error("🔐 callCloudRunFunction - Failed to get token");
    throw new Error("Failed to get authentication token");
  }

  console.log(
    "🔐 callCloudRunFunction - Token retrieved, length:",
    token.length,
  );
  console.log("🔐 callCloudRunFunction - Calling URL:", url);

  // Firebase callable functions expect this exact format
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": "blinkbli",
    },
    body: JSON.stringify({ data }),
  });

  console.log("🔐 callCloudRunFunction - Response status:", response.status);

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      const errorText = await response.text();
      throw new Error(`Function call failed: ${response.status} ${errorText}`);
    }

    // Firebase callable functions return errors in this format
    if (errorData.error) {
      const error = errorData.error;
      throw new Error(
        error.message || `Function error: ${error.code || response.status}`,
      );
    }

    throw new Error(`Function call failed: ${response.status}`);
  }

  const result = await response.json();
  // Firebase callable functions return data in a { result: ... } format
  if (result.result !== undefined) {
    return result.result;
  }
  // Fallback for direct response
  return result;
}

/**
 * Helper to call Firebase callable functions properly
 * Uses httpsCallable for proper CORS handling
 * Automatically adds tenantId for blinkbli functions
 */
function callFunction<T = any, R = any>(functionName: string, data?: T): Promise<R> {
  const callable = httpsCallable<T, R>(functions, functionName);
  // Add tenantId to data for blinkbli tenant functions
  const dataWithTenant = { ...data, tenantId: 'blinkbli' } as T;
  return callable(dataWithTenant).then((result) => result.data);
}

// Helpers for blinkbli tenant callables using proper Firebase SDK
export const qrJoinRoomFn = async (data: {
  code: string;
  deviceId: string;
  username: string;
  location: { lat: number; lng: number };
  tenantId?: string;
}) => callFunction("qrJoinRoom", data);

export const qrPostMessageFn = async (data: {
  code: string;
  deviceId: string;
  text: string;
  clientMessageId: string;
}) => callFunction("qrPostMessage", data);

export const qrListJoinedRoomsFn = async (
  data?: { tenantId?: string },
  _user?: User | null,
) => callFunction("qrListJoinedRooms", data || {});

export const qrSetUsernameFn = async (data: {
  code: string;
  username: string;
}) => callFunction("qrSetUsername", data);

export const qrGetRoomMembersFn = async (data: { code: string }) =>
  callFunction("qrGetRoomMembers", data);

export const qrGetRoomDetailsFn = async (data: { code: string }) =>
  callFunction("qrGetRoomDetails", data);

export const qrAdminListRoomsFn = async (data?: { adminKey?: string }) =>
  callFunction("qrAdminListRooms", data || {});

export const qrAdminDeleteRoomFn = async (data: {
  code: string;
  adminKey?: string;
}) => callFunction("qrAdminDeleteRoom", data);

export const qrAdminGenerateQRDataFn = async (data: { code: string }) =>
  callFunction("qrAdminGenerateQRData", data);

export const qrRegisterPushTokenFn = async (data: {
  token: string;
  deviceId: string;
}) => callFunction("qrRegisterPushToken", data);

async function getMessagingInstance(): Promise<Messaging | null> {
  if (typeof window === "undefined") {
    return null;
  }
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then((supported) => {
        if (!supported) {
          console.warn("Firebase messaging is not supported in this browser.");
          return null;
        }
        return getMessaging(app);
      })
      .catch((err) => {
        console.error("Failed to initialize Firebase messaging:", err);
        return null;
      });
  }
  return messagingPromise;
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  try {
    const existing = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    if (existing) {
      return existing;
    }
    return await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/",
    });
  } catch (err) {
    console.error("Failed to register messaging service worker:", err);
    return null;
  }
}

/**
 * Returns the current FCM token, registering the service worker when needed.
 */
export async function getFcmToken(): Promise<string | null> {
  const messaging = await getMessagingInstance();
  if (!messaging) {
    return null;
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.warn("VITE_FIREBASE_VAPID_KEY is not set. Push reminders disabled.");
    return null;
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return null;
  }

  try {
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (err) {
    console.error("Failed to fetch FCM token:", err);
    return null;
  }
}

/**
 * Ensures the current device is registered for daily reminder pushes.
 */
export async function ensureReminderPushRegistration(deviceId: string) {
  if (typeof window === "undefined") {
    return;
  }
  const token = await getFcmToken();
  if (!token) {
    return;
  }

  const cachedToken = localStorage.getItem(LOCAL_FCM_TOKEN_KEY);
  if (cachedToken === token) {
    return;
  }

  try {
    await qrRegisterPushTokenFn({ token, deviceId });
    localStorage.setItem(LOCAL_FCM_TOKEN_KEY, token);
  } catch (err) {
    console.error("Failed to register push token:", err);
  }
}
